// 'local' strategy — intranet node mode (BRIEF owner addition B). erp-core's
// internal/auth/local is the contract:
//
//   POST /api/erp/auth/login   { phone: "+9647…", pin: "123456", device? }
//   POST /api/erp/auth/unlock  { pin }   (bearer of the idle-locked session)
//   → 200 { token, token_type: "Bearer", expires_in, idle_lock_seconds,
//           user: { id, phone, name },
//           memberships: [{ business_id, business_name, role }] }
//   Problems (RFC 9457, `code` is the contract):
//     login  401 invalid_credentials · 429 pin_locked + Retry-After header ·
//            422 validation_failed with errors[{field:"phone", code:"invalid_phone"}]
//     unlock 403 invalid_pin · 429 pin_locked + Retry-After ·
//            401 session_revoked | session_expired | invalid_token | unauthorized
//   POST /api/erp/auth/logout  (bearer; 204 — best effort)
//
// Also used under the mock interceptor (mocks can't fake Authentik's flow).
// Self-registers on import.

import { API_BASE } from '../env';
import { normalizeDigits, normalizeIraqiPhone } from '../digits';
import { lockedMessage } from './authentik_flow';
import { registerStrategy } from './registry';
import { sessionStore } from './session';
import { AuthError, AUTH_COPY, type AuthStrategy, type LoginResult, type SessionMembership } from './types';

/** erp-core internal/auth/local.SessionResponse. */
export interface LocalSessionResponse {
  token: string;
  token_type: string;
  expires_in: number;
  idle_lock_seconds: number;
  user: { id: string; phone: string; name: string };
  memberships: Array<{ business_id: string; business_name: string; role: string }> | null;
}

interface Problem {
  code?: string;
  detail?: string;
  errors?: Array<{ field?: string; code?: string }>;
}

/** Lockout fallback when a 429 carries no usable Retry-After. */
const DEFAULT_LOCK_SECONDS = 60;

async function post(path: string, body: unknown, token?: string): Promise<Response> {
  return fetch(`${API_BASE}/erp${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
}

/** Retry-After is delta-seconds (erp-core) or an HTTP date (RFC 9110 §10.2.3). */
export function retryAfterSeconds(header: string | null, now = Date.now()): number | null {
  if (!header) return null;
  const v = header.trim();
  if (/^\d+$/.test(v)) return Math.max(1, Number(v));
  const at = Date.parse(v);
  if (Number.isNaN(at)) return null;
  return Math.max(1, Math.ceil((at - now) / 1000));
}

function toMemberships(raw: LocalSessionResponse['memberships']): SessionMembership[] {
  const out: SessionMembership[] = [];
  for (const m of raw ?? []) {
    if (!m?.business_id || (m.role !== 'owner' && m.role !== 'manager' && m.role !== 'staff')) continue;
    out.push({ business_id: m.business_id, business_name: m.business_name ?? '', role: m.role });
  }
  return out;
}

export function toLoginResult(data: LocalSessionResponse, phone: string): LoginResult {
  if (!data?.token) throw new AuthError('unexpected', AUTH_COPY.unexpected);
  const name = data.user?.name || phone;
  return {
    tokens: { accessToken: data.token, expiresIn: data.expires_in ?? 0 },
    user: { id: data.user?.id, name, phone: data.user?.phone || phone, initial: name.trim().charAt(0) || '؟' },
    memberships: toMemberships(data.memberships),
    idleLockSeconds: data.idle_lock_seconds > 0 ? data.idle_lock_seconds : undefined,
  };
}

async function readProblem(res: Response): Promise<Problem> {
  try {
    return ((await res.json()) ?? {}) as Problem;
  } catch {
    return {};
  }
}

function lockedError(res: Response): AuthError {
  const secs = retryAfterSeconds(res.headers.get('Retry-After')) ?? DEFAULT_LOCK_SECONDS;
  return new AuthError('locked', lockedMessage(secs), secs);
}

export const localStrategy: AuthStrategy = {
  name: 'local',
  async login(phone, pin) {
    const e164 = normalizeIraqiPhone(phone);
    if (!e164) throw new AuthError('invalid_phone', AUTH_COPY.invalidPhone);
    let res: Response;
    try {
      res = await post('/auth/login', { phone: e164, pin: normalizeDigits(pin) });
    } catch {
      throw new AuthError('network', AUTH_COPY.network);
    }
    if (res.ok) return toLoginResult((await res.json()) as LocalSessionResponse, e164);
    if (res.status === 429) throw lockedError(res);
    const problem = await readProblem(res);
    if (res.status === 422 && problem.errors?.some((e) => e.field === 'phone')) {
      throw new AuthError('invalid_phone', AUTH_COPY.invalidPhone);
    }
    if (res.status === 401 || problem.code === 'invalid_credentials') {
      throw new AuthError('invalid_credentials', AUTH_COPY.invalidPin);
    }
    if (res.status === 403) throw new AuthError('denied', AUTH_COPY.denied);
    throw new AuthError('unexpected', AUTH_COPY.unexpected);
  },
  async unlock(pin, accessToken) {
    let res: Response;
    try {
      res = await post('/auth/unlock', { pin: normalizeDigits(pin) }, accessToken);
    } catch {
      throw new AuthError('network', AUTH_COPY.network);
    }
    const phone = sessionStore.getUser()?.phone ?? '';
    if (res.ok) return toLoginResult((await res.json()) as LocalSessionResponse, phone);
    if (res.status === 429) throw lockedError(res);
    // 403 invalid_pin: wrong PIN, session still alive (stay on the lock).
    if (res.status === 403) throw new AuthError('invalid_credentials', AUTH_COPY.invalidPin);
    // 401 session_revoked / session_expired / invalid_token: the session is gone.
    if (res.status === 401) throw new AuthError('session_ended', AUTH_COPY.sessionEnded);
    throw new AuthError('unexpected', AUTH_COPY.unexpected);
  },
  async refresh() {
    // Node sessions are HS256 tokens with a fixed lifetime; renewal = unlock with the PIN.
    return null;
  },
  async logout() {
    try {
      await post('/auth/logout', {}, sessionStore.getToken() ?? undefined);
    } catch {
      /* best effort */
    }
  },
};

registerStrategy('local', () => localStrategy);
